import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Plus,
  Video,
  User,
  Calendar,
  Clock,
  Sparkles,
  Loader2,
  CheckCircle2,
  Info,
  Camera,
  Activity,
  Heart
} from "lucide-react";
import { apiHandler } from "../../services/authService";
import { fetchUserInfo, fetchProviderInfo } from "../../services/providerService";
import { BASE_URL } from "../interface/Config";
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from "@mui/material";
import { checkSlotAvailability } from "../../utils/availability";
import { ProviderInfo } from "../interface/providerinterface";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

interface Patient {
  id: number;
  user_id: number;
  hospital_id: number;
  additional_info: {
    age: string;
    uhid_number: string;
  };
  user: {
    first_name: string;
    last_name: string;
    gender: string;
  };
}

interface CameraItem {
  id: number;
  hospital_id: number;
  camera_name: string;
  camera_ip: string;
  camera_short_name: string;
  camera_type: string;
}

const SPECIALITIES = [
  { label: "Neurology", value: "neurology" },
  { label: "Cardiology", value: "cardiology" },
  { label: "General Medicine", value: "general medicine" },
  { label: "Pediatrics", value: "pediatrics" },
  { label: "Dermatology", value: "dermatology" },
  { label: "Oncology", value: "oncology" },
  { label: "Psychiatry", value: "psychiatry" },
  { label: "Orthopedics", value: "orthopedics" },
  { label: "Ophthalmology", value: "ophthalmology" }
];

export const CreateConsultModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const token = sessionStorage.getItem("authToken") || "";
  const now = new Date();
  const year = now.getFullYear();
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  const localDayVal = now.getDate().toString().padStart(2, "0");
  const today = `${year}-${month}-${localDayVal}`;
  const currentHour = now.getHours().toString().padStart(2, "0");
  const currentMinute = now.getMinutes().toString().padStart(2, "0");
  const defaultTime = `${currentHour}:${currentMinute}`;

  // Form States
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedCamera, setSelectedCamera] = useState<CameraItem | null>(null);
  const [speciality, setSpeciality] = useState<string>("general medicine");
  const [reason, setReason] = useState<string>("");
  const [date, setDate] = useState<string>(today);
  const [time, setTime] = useState<string>(defaultTime);

  // Dynamic Data & Status States
  const [patients, setPatients] = useState<Patient[]>([]);
  const [cameras, setCameras] = useState<CameraItem[]>([]);
  const [providerId, setProviderId] = useState<number | null>(null);
  const [providerData, setProviderData] = useState<ProviderInfo["data"] | null>(null);

  const [loadingLists, setLoadingLists] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState<boolean>(false);

  // Load provider details, patients, and cameras on mount
  useEffect(() => {
    const loadRequiredData = async () => {
      try {
        setLoadingLists(true);
        setError(null);

        // 1. Fetch Logged-in Doctor User and Provider ID
        const userInfo = await fetchUserInfo();
        if (userInfo?.data?.id) {
          const providerInfo = await fetchProviderInfo(userInfo.data.id);
          if (providerInfo?.data?.id) {
            setProviderId(providerInfo.data.id);
            setProviderData(providerInfo.data);
          }
        }

        // 2. Fetch Patients List
        const patientsUrl = `${BASE_URL}/resource/patients/all`;
        const patientsRes = await apiHandler(token, "GET", {}, patientsUrl);
        if (patientsRes?.data?.data?.patients) {
          setPatients(patientsRes.data.data.patients);
        } else if (patientsRes?.data?.data) {
          // Handle alternate nested array structures safely
          const list = Array.isArray(patientsRes.data.data) ? patientsRes.data.data : [];
          setPatients(list);
        }

        // 3. Fetch Cameras List
        const camerasUrl = `${BASE_URL}/resource/cameras/all`;
        const camerasRes = await apiHandler(token, "GET", {}, camerasUrl);
        if (camerasRes?.data?.data?.cameras) {
          setCameras(camerasRes.data.data.cameras);
        } else if (camerasRes?.data?.data) {
          const list = Array.isArray(camerasRes.data.data) ? camerasRes.data.data : [];
          setCameras(list);
        }

      } catch (err: any) {
        console.error("Error loading consult options:", err);
        setError("Failed to load patient or clinical camera registers. Please refresh.");
      } finally {
        setLoadingLists(false);
      }
    };

    if (token) {
      loadRequiredData();
    } else {
      setError("Authorization credentials missing.");
      setLoadingLists(false);
    }
  }, [token]);

  // Update provider data when the selected consult date changes to load appropriate unavailabilities
  useEffect(() => {
    if (!token || !providerId) return;

    const updateProviderDataForDate = async () => {
      try {
        const userInfo = await fetchUserInfo();
        if (userInfo?.data?.id) {
          const providerInfo = await fetchProviderInfo(userInfo.data.id, date);
          if (providerInfo?.data) {
            setProviderData(providerInfo.data);
          }
        }
      } catch (err) {
        console.error("Error updating provider info for date:", err);
      }
    };

    updateProviderDataForDate();
  }, [date, providerId, token]);

  // Fetch available slots from API
  useEffect(() => {
    if (!token || !date || !speciality || !providerId) {
      setAvailableSlots([]);
      return;
    }

    const fetchSlots = async () => {
      try {
        setLoadingSlots(true);
        const url = `${BASE_URL}/resource/available-slots`;
        const params = {
          consult_date: date,
          speciality: speciality,
          provider_id: providerId
        };
        const res = await apiHandler(token, "GET", params, url);
        if (res?.data?.data?.slots && Array.isArray(res.data.data.slots)) {
          let slots = res.data.data.slots;

          // Filter out slots that are in the past if the selected date is today
          if (date === today) {
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();
            slots = slots.filter((slot: string) => {
              const [slotH, slotM] = slot.split(":").map(Number);
              if (slotH > currentHour) return true;
              if (slotH === currentHour && slotM >= currentMinute) return true;
              return false;
            });
          }

          setAvailableSlots(slots);
          // Auto-select first slot if current selected slot is not in the list
          if (slots.length > 0) {
            if (!slots.includes(time)) {
              setTime(slots[0]);
            }
          } else {
            setTime("");
          }
        } else {
          setAvailableSlots([]);
          setTime("");
        }
      } catch (err: any) {
        console.error("Error fetching available slots:", err);
        setAvailableSlots([]);
        setTime("");
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [date, speciality, providerId, token]);

  const validate = (): string | null => {
    if (!providerId) return "Doctor clinical profile not loaded. Please wait.";
    if (!selectedPatient) return "Please search and select a patient.";
    if (!selectedCamera) return "Please choose a clinical IP camera/cart.";
    if (!speciality) return "Please select a specialty.";
    if (!reason.trim()) return "Please state the reason for consultation.";
    if (!date) return "Please choose a consultation date.";
    if (!time) return "Please choose an available consultation time slot.";

    // Check provider availability via slots
    if (availableSlots.length > 0 && !availableSlots.includes(time)) {
      return "The selected time slot is not available. Please choose a valid slot.";
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);

      // Formulate ISO consult date string (UTC)
      const localDateTimeStr = `${date}T${time}:00`;
      const consultDateISO = new Date(localDateTimeStr).toISOString();

      // Formulate formatted Start & End times: "YYYY-MM-DD HH:mm:ss"
      const formattedStart = `${date} ${time}:00`;

      // Compute end time: start time + 15 minutes
      const startDateObj = new Date(localDateTimeStr);
      startDateObj.setMinutes(startDateObj.getMinutes() + 15);
      const endYear = startDateObj.getFullYear();
      const endMonth = (startDateObj.getMonth() + 1).toString().padStart(2, "0");
      const endDay = startDateObj.getDate().toString().padStart(2, "0");
      const endHours = startDateObj.getHours().toString().padStart(2, "0");
      const endMinutes = startDateObj.getMinutes().toString().padStart(2, "0");
      const formattedEnd = `${endYear}-${endMonth}-${endDay} ${endHours}:${endMinutes}:00`;

      // Construct API payload matching exact format requested
      const payload = {
        consult_date: consultDateISO,
        consult_date_time: formattedStart,
        consult_end_time: formattedEnd,
        consult_time: time,
        provider_id: providerId,
        patient_id: selectedPatient!.user_id,
        speciality: speciality,
        reason_for_consult: reason,
        cart_camera: {
          id: selectedCamera!.id,
          hospital_id: selectedCamera!.hospital_id,
          camera_name: selectedCamera!.camera_name,
          camera_ip: selectedCamera!.camera_ip,
          camera_short_name: selectedCamera!.camera_short_name,
          camera_type: selectedCamera!.camera_type
        },
        patient_name: {
          id: selectedPatient!.id,
          user_id: selectedPatient!.user_id,
          hospital_id: selectedPatient!.hospital_id,
          additional_info: {
            age: selectedPatient!.additional_info?.age || "N/A",
            uhid_number: selectedPatient!.additional_info?.uhid_number || "skip"
          },
          user: {
            first_name: selectedPatient!.user.first_name,
            last_name: selectedPatient!.user.last_name,
            gender: selectedPatient!.user.gender
          }
        },
        queueslots: {
          from_time: "00:00",
          to_time: "23:59"
        }
      };

      const consultUrl = `${BASE_URL}/resource/consults`;
      const response = await apiHandler(token, "POST", payload, consultUrl);

      if (response) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        throw new Error("Unable to save consultation record.");
      }

    } catch (err: any) {
      console.error("Error creating teleconsultation:", err);
      setError(
        err?.message ||
        "Failed to schedule teleconsultation. Verify the clinical registers and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isAvailable = !time || availableSlots.length === 0 || availableSlots.includes(time);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 42, 0.4)", backdropFilter: "blur(8px)" }}
      onClick={(e: React.MouseEvent<HTMLDivElement>) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden font-primary border border-slate-100 flex flex-col max-h-[90vh] sm:max-h-[85vh]"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-slate-900 px-6 sm:px-8 pt-8 pb-6 relative overflow-hidden shrink-0">
          <Activity
            size={160}
            className="absolute -right-8 -bottom-8 text-white/5 pointer-events-none"
          />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2.5 bg-violet-500/20 rounded-xl inline-flex border border-violet-500/10">
                <Video size={22} className="text-violet-400" />
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>
            <h2 className="text-2xl font-black text-white tracking-tight mb-1">
              New Video Teleconsultation
            </h2>
            <p className="text-white/50 text-sm font-medium">
              Schedule clinical sessions and map hardware cart endpoints.
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loadingLists ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <Loader2 size={36} className="text-violet-600 animate-spin" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
              Fetching Registers...
            </p>
          </div>
        ) : (
          /* Form Body */
          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 overflow-y-auto flex-1">
            {success ? (
              <div className="flex flex-col items-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} className="text-emerald-500" />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-1">
                  Consultation Scheduled
                </h3>
                <p className="text-slate-400 text-sm font-medium">
                  The appointment records have been integrated successfully.
                </p>
              </div>
            ) : (
              <>
                {/* Error Banner */}
                {error && (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                    <Info size={16} className="shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* Section 1: Patient Details */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <User size={16} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                      Patient Mapping
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Search Patient
                      </label>
                      <Autocomplete
                        options={patients}
                        getOptionLabel={(p) =>
                          `${p.user?.first_name || ""} ${p.user?.last_name || ""} (ID: ${p.user_id || ""})`
                        }
                        value={selectedPatient}
                        onChange={(_, value) => setSelectedPatient(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Type to search patients by name..."
                            variant="outlined"
                            className="bg-slate-50 rounded-2xl overflow-hidden"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                padding: "6px 12px",
                                "& fieldset": { border: "none" }
                              }
                            }}
                          />
                        )}
                        noOptionsText="No clinical patient matches found"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Clinical Mapping */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Camera size={16} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                      Consultation details & Cart camera
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Specialty
                      </label>
                      <select
                        value={speciality}
                        onChange={(e) => setSpeciality(e.target.value)}
                        className="w-full h-[45px] px-4 rounded-2xl bg-slate-50 border-none text-slate-700 font-bold text-sm focus:ring-2 focus:ring-violet-500/20 transition-all cursor-pointer"
                      >
                        {SPECIALITIES.map((spec) => (
                          <option key={spec.value} value={spec.value}>
                            {spec.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                        Select IP Camera / Cart
                      </label>
                      <Autocomplete
                        options={cameras}
                        getOptionLabel={(cam) =>
                          `${cam.camera_name || ""} - ${cam.camera_ip || ""}`
                        }
                        value={selectedCamera}
                        onChange={(_, value) => setSelectedCamera(value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search carts..."
                            variant="outlined"
                            className="bg-slate-50 rounded-2xl overflow-hidden"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                padding: "6px 12px",
                                "& fieldset": { border: "none" }
                              }
                            }}
                          />
                        )}
                        noOptionsText="No camera cart endpoints available"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      Reason for Consult
                    </label>
                    <textarea
                      rows={2}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Input clinical indications, chief complaints, or reason for consult..."
                      className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200/50 text-slate-700 font-medium text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/50 transition-all resize-none"
                    />
                  </div>
                </div>

                {/* Section 3: Time Slot Mapping */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
                    <Calendar size={16} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
                      Schedule Configuration
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                        <Calendar size={12} className="text-violet-500" /> Consult Date
                      </label>
                      <input
                        type="date"
                        value={date}
                        min={today}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full px-4 py-3 rounded-2xl bg-slate-50 border border-slate-200/50 text-slate-700 font-bold text-sm focus:ring-2 focus:ring-violet-500/20 transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                        <Clock size={12} className="text-violet-500" /> Available Time Slots
                      </label>
                      {loadingSlots ? (
                        <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                          <Loader2 size={16} className="text-violet-600 animate-spin" />
                          <span className="text-xs font-semibold text-slate-500">Loading slots...</span>
                        </div>
                      ) : availableSlots.length > 0 ? (
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[160px] overflow-y-auto p-1.5 border border-slate-100/80 rounded-2xl bg-slate-50/50">
                          {availableSlots.map((slot) => {
                            const isSelected = time === slot;
                            return (
                              <button
                                key={slot}
                                type="button"
                                onClick={() => setTime(slot)}
                                className={`py-2 px-3 text-xs font-bold rounded-xl transition-all border text-center ${isSelected
                                    ? "bg-violet-600 border-violet-600 text-white shadow-md shadow-violet-500/20"
                                    : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200 hover:border-violet-300"
                                  }`}
                              >
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold flex items-start gap-2.5 shadow-sm">
                          <Info size={16} className="shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold">No slots available</p>
                            <p className="text-[10px] text-amber-700 font-medium mt-0.5">
                              There are no available slots for the selected date, specialty, or doctor clinical hours.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isAvailable && (
                    <div className="p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-750 text-xs font-semibold flex items-start gap-2.5 shadow-sm mt-3 animate-pulse">
                      <Info size={16} className="shrink-0 mt-0.5" />
                      <div>
                        <p className="font-bold leading-none mb-1">Time Slot Unavailable</p>
                        <p className="text-[10px] text-rose-600 font-medium mt-0.5">
                          The selected date/time falls outside your weekly active schedule or coincides with a registered leave/time-off block.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview Panel */}
                {selectedPatient && selectedCamera && (
                  <div className="p-4 rounded-2xl bg-violet-500/5 border border-violet-500/10 space-y-1.5">
                    <div className="flex items-center gap-1">
                      <Sparkles size={12} className="text-violet-500 animate-pulse" />
                      <p className="text-[9px] font-black text-violet-600 uppercase tracking-widest">
                        Scheduling Live Summary
                      </p>
                    </div>
                    <p className="text-xs font-bold text-slate-800">
                      👨‍⚕️ Patient: <span className="text-slate-900">{selectedPatient.user.first_name} {selectedPatient.user.last_name}</span> | age: {selectedPatient.additional_info?.age || "N/A"}
                    </p>
                    <p className="text-xs font-bold text-slate-800">
                      📹 Camera IP: <span className="text-slate-900 font-black">{selectedCamera.camera_ip}</span> ({selectedCamera.camera_name})
                    </p>
                    <p className="text-xs font-bold text-slate-800">
                      📅 Date/Time: <span className="text-slate-900">{date} at {time}</span> (Slot duration: 15 minutes)
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2 shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 h-12 rounded-2xl border border-slate-200 text-slate-500 font-bold text-sm hover:bg-slate-50 transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-[2] h-12 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white font-black text-sm shadow-lg shadow-violet-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Scheduling…
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Schedule Consultation
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </motion.div>
    </div>
  );
};
